/**
 * Calculate the percentage of original text that remains in edited text
 * Uses word-level comparison to determine similarity
 */

/**
 * Normalize text for comparison - lowercase, remove extra whitespace
 */
const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Split text into words
 */
const getWords = (text) => {
  const normalized = normalizeText(text);
  if (!normalized) return [];
  return normalized.split(' ').filter(w => w.length > 0);
};

/**
 * Calculate Longest Common Subsequence length using dynamic programming
 * This measures how much of the original text sequence is preserved
 */
const lcsLength = (arr1, arr2) => {
  const m = arr1.length;
  const n = arr2.length;

  if (m === 0 || n === 0) return 0;

  // Create DP table
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (arr1[i - 1] === arr2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp[m][n];
};

/**
 * Calculate what percentage of the original content is preserved in the edited version
 * Returns a number between 0 and 100
 *
 * @param {string} original - The original text
 * @param {string} edited - The edited text
 * @returns {number} - Percentage of original preserved (0-100)
 */
export const calculatePercentOriginal = (original, edited) => {
  // If no original, can't calculate
  if (!original) return null;

  // If texts are identical, 100% original
  if (normalizeText(original) === normalizeText(edited)) return 100;

  // If edited is empty, 0% original
  if (!edited || normalizeText(edited) === '') return 0;

  const originalWords = getWords(original);
  const editedWords = getWords(edited);

  // If original has no words, can't calculate
  if (originalWords.length === 0) return null;

  // Calculate LCS length - how many original words are preserved in order
  const lcs = lcsLength(originalWords, editedWords);

  // Calculate percentage based on how much of original is preserved
  const percent = Math.round((lcs / originalWords.length) * 100);

  return Math.min(100, Math.max(0, percent));
};

/**
 * Check if content has been edited from original
 *
 * @param {string} original - The original text
 * @param {string} current - The current text
 * @returns {boolean} - True if content has been edited
 */
export const isEdited = (original, current) => {
  if (!original) return false;
  return normalizeText(original) !== normalizeText(current);
};

/**
 * Format the % original for display
 *
 * @param {number|null} percent - The percentage (0-100) or null
 * @returns {string} - Formatted string like "85% original" or empty string
 */
export const formatPercentOriginal = (percent) => {
  if (percent === null || percent === undefined) return '';
  if (percent === 100) return '';
  return `${percent}% original`;
};
